import { z } from "zod";
import { FingerAnalysisSchema, FingerKeySchema, KlienRecordSchema, PatternSchema, Point2DSchema, REPORT_TIERS, RoleSchema, SCAN_SIDES, } from "./domain.js";
// ============================================================================
// Auth API
// ============================================================================
export const LoginRequestSchema = z.object({
    email: z.string().email(),
    password: z.string().min(8).max(256),
});
export const LoginResponseSchema = z.object({
    access_token: z.string(),
    refresh_token: z.string(),
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        name: z.string(),
        role: RoleSchema,
        tenant_id: z.string().uuid(),
        franchisor_id: z.string().uuid().nullable(),
        scope_type: z.enum(["dcppi", "franchisor"]),
    }),
});
// ============================================================================
// Klien API
// ============================================================================
export const CreateKlienRequestSchema = z.object({
    full_name: z.string().min(1).max(255),
    dob: z.string(),
    parent_name: z.string().optional(),
    parent_phone: z.string().regex(/^\+?[0-9\s\-()]{6,20}$/).optional(),
    school_name: z.string().optional(),
    is_left_handed: z.boolean().default(false),
    package_tier: z.enum(REPORT_TIERS),
    consent_given: z.literal(true),
});
export const KlienQueueItemSchema = KlienRecordSchema.extend({
    finger_summary: z.object({
        total: z.literal(10),
        analyzed: z.number().int().min(0).max(10),
        approved: z.number().int().min(0).max(10),
        failed: z.number().int().min(0).max(10),
        /** Lowest confidence among analyzed fingers. */
        min_confidence: z.number().min(0).max(1).nullable(),
        /** Count of fingers needing review (<92%). */
        needs_review_count: z.number().int().min(0).max(10),
    }),
});
export const KlienListResponseSchema = z.object({
    items: z.array(KlienQueueItemSchema),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    per_page: z.number().int().min(1).max(200),
});
// ============================================================================
// Upload API (scan station)
// ============================================================================
export const UploadFingerImageRequestSchema = z.object({
    klien_id: z.string().uuid(),
    finger: FingerKeySchema,
    side: z.enum(SCAN_SIDES),
    /** Image as multipart upload — handled at HTTP layer. */
    filename: z.string(),
    content_type: z.string().regex(/^image\/(jpe?g|png)$/),
});
export const UploadFingerImageResponseSchema = z.object({
    upload_id: z.string().uuid(),
    storage_path: z.string(),
    stitch_job_enqueued: z.boolean(),
});
// ============================================================================
// Layer 4 Dashboard API
// ============================================================================
export const KlienAnalysisResponseSchema = z.object({
    klien: KlienRecordSchema,
    fingers: z.array(FingerAnalysisSchema.extend({
        stitched_image_url: z.string().url().nullable(),
        regenerate_svg: z.string().nullable(),
        regenerate_url: z.string().url().nullable(),
        gabor_smooth_url: z.string().nullable().optional(),
        finger_status: z.string(),
        sub_version: z.number().int().min(0),
        // Opsi X: which sides were actually uploaded (ordered F,L,R) + which one fed
        // geometry (= composite panel index, for the dynamic marker overlay).
        sides_present: z.array(z.enum(["F", "L", "R"])).default([]),
        n_sides: z.number().int().default(0),
        geometry_side: z.enum(["F", "L", "R"]).nullable().default(null),
        geometry_side_index: z.number().int().default(0),
        // #2 layout: URL foto sisi mentah (hanya sisi yang di-upload).
        raw_side_urls: z.object({
            F: z.string().optional(),
            L: z.string().optional(),
            R: z.string().optional(),
        }).default({}),
    })),
    permissions: z.object({
        can_edit: z.boolean(),
        can_approve: z.boolean(),
        can_flag_back: z.boolean(),
        can_void: z.boolean(),
    }),
    /** Per-finger upload state: side keys present per finger that already exist
     *  in finger_uploads. Used to render Upload UI for klien in pre-ready state. */
    upload_progress: z.record(z.string(), z.array(z.enum(["F", "L", "R"]))).default({}),
});
/** Layer 4 edit payload — autosaved on each interaction. */
export const FingerEditRequestSchema = z.object({
    klien_id: z.string().uuid(),
    finger: FingerKeySchema,
    /** Client-generated edit ID for idempotency / deduping. */
    edit_id: z.string().uuid(),
    /** Optimistic concurrency: latest sub_version the client saw. */
    expected_sub_version: z.number().int().min(0),
    changes: z.object({
        pattern: PatternSchema.optional(),
        ridge_count_left: z.number().int().min(0).optional(),
        ridge_count_right: z.number().int().min(0).optional(),
        core: Point2DSchema.nullable().optional(),
        deltas: z
            .array(z.object({
            x: z.number(),
            y: z.number(),
            label: z.enum(["kiri", "kanan"]),
        }))
            .optional(),
        hand_override: z.enum(["right", "left"]).optional(),
    }),
});
export const FingerEditResponseSchema = z.object({
    finger: FingerKeySchema,
    sub_version: z.number().int().min(0),
    applied: z.boolean(),
    conflict: z
        .object({
        latest_sub_version: z.number().int().min(0),
        latest_state: FingerAnalysisSchema,
    })
        .optional(),
    recomputed: z
        .object({
        ridge_count_left: z.number().int().min(0),
        ridge_count_right: z.number().int().min(0),
    })
        .optional(),
});
export const ApproveKlienRequestSchema = z.object({
    klien_id: z.string().uuid(),
    /** Optimistic concurrency at klien level. */
    expected_version: z.number().int().min(0),
    /** Klien initials confirmation per design doc (anti-fat-finger). */
    klien_initials_confirmation: z.string().min(1).max(8),
    /** Optional reviewer self-confidence (1-5) in this review's corrections,
     *  stored on every training_queue row for retraining prioritisation. */
    reviewer_confidence_rating: z.number().int().min(1).max(5).optional(),
});
export const ApproveKlienResponseSchema = z.object({
    klien_id: z.string().uuid(),
    status: z.literal("approved"),
    share_link_url: z.string().url(),
    share_link_expires_at: z.string().datetime(),
    report_pdf_pending: z.boolean(),
});
export const FlagBackRequestSchema = z.object({
    klien_id: z.string().uuid(),
    reason: z.string().min(3).max(500),
});
// ============================================================================
// Share link (klien view) API
// ============================================================================
export const ShareTokenValidationRequestSchema = z.object({
    token: z.string().min(10),
});
export const ShareTokenValidationResponseSchema = z.object({
    klien_full_name: z.string(),
    /** Masked phone, e.g. "+62812****890". */
    parent_phone_masked: z.string(),
    otp_required: z.boolean(),
    expires_at: z.string().datetime(),
});
export const ShareOtpRequestSchema = z.object({
    token: z.string(),
});
export const ShareOtpVerifyRequestSchema = z.object({
    token: z.string(),
    otp_code: z.string().regex(/^[0-9]{6}$/),
});
export const ShareReportResponseSchema = z.object({
    klien_full_name: z.string(),
    package_tier: z.enum(REPORT_TIERS),
    report_html: z.string(),
    report_pdf_url: z.string().url().optional(),
    rotated_token: z.string(),
    next_expires_at: z.string().datetime(),
});
// ============================================================================
// Audit log API
// ============================================================================
export const AuditLogQuerySchema = z.object({
    klien_id: z.string().uuid().optional(),
    actor_id: z.string().uuid().optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    page: z.coerce.number().int().min(1).default(1),
    per_page: z.coerce.number().int().min(1).max(200).default(50),
});
// ============================================================================
// API error envelope
// ============================================================================
export const ApiErrorSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.record(z.unknown()).optional(),
    }),
});
/** Standard error codes. */
export const ERROR_CODES = {
    UNAUTHORIZED: "UNAUTHORIZED",
    FORBIDDEN: "FORBIDDEN",
    NOT_FOUND: "NOT_FOUND",
    CONFLICT: "CONFLICT",
    VERSION_CONFLICT: "VERSION_CONFLICT",
    VALIDATION_ERROR: "VALIDATION_ERROR",
    RATE_LIMITED: "RATE_LIMITED",
    INTERNAL_ERROR: "INTERNAL_ERROR",
    OTP_REQUIRED: "OTP_REQUIRED",
    OTP_INVALID: "OTP_INVALID",
    OTP_EXPIRED: "OTP_EXPIRED",
    TOKEN_EXPIRED: "TOKEN_EXPIRED",
    TENANT_SCOPE_VIOLATION: "TENANT_SCOPE_VIOLATION",
};
//# sourceMappingURL=api.js.map